import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { Link, useSearchParams } from "react-router-dom";
import SportArcSlider from "../components/SportArcSlider";
import AccessKeyModal from "../components/AccessKeyModal";

const LightRays = () => {
  const rayCount = 8;
  const rays = Array.from({ length: rayCount }).map((_, i) => {
    return { angle: (360 / rayCount) * i };
  });

  return (
    <svg
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        pointerEvents: "none",
        zIndex: 9200,
        overflow: "visible",
      }}
    >
      {rays.map(({ angle }, i) => (
        <motion.line
          key={i}
          x1="50%"
          y1="50%"
          x2="50%"
          y2="10%"
          stroke="rgba(255,240,180,0.5)"
          strokeWidth={4}
          strokeLinecap="round"
          style={{ originX: "50%", originY: "50%" }}
          initial={{ rotate: angle - 20, opacity: 0, scale: 0.6 }}
          animate={{ rotate: angle, opacity: 1, scale: 1 }}
          transition={{
            delay: 3 + i * 0.2,
            repeat: Infinity,
            repeatType: "reverse",
            duration: 3,
            ease: "easeInOut",
          }}
        />
      ))}
    </svg>
  );
};


const TypewriterText = ({ words, onComplete }) => {
  const [wordIndex, setWordIndex] = useState(0);
  const [charCount, setCharCount] = useState(0);
  const intervalRef = useRef();

  useEffect(() => {
    if (wordIndex >= words.length) {
      if (onComplete) onComplete();
      return;
    }
    intervalRef.current = setInterval(() => {
      setCharCount(count => {
        if (count < words[wordIndex].length) return count + 1;
        clearInterval(intervalRef.current);
        setTimeout(() => setWordIndex(i => i + 1), 400);
        return count;
      });
    }, wordIndex === words.length - 1 ? 160 : 280);
    return () => clearInterval(intervalRef.current);
  }, [wordIndex, words, onComplete]);

  return (
    <div style={{
      position: "fixed",
      top: "20vh",
      width: "100vw",
      textAlign: "center",
      fontFamily: "'Major Mono Display', monospace",
      fontWeight: "800",
      color: "#66ccff",
      textShadow: "0 0 12px #44aaff, 0 0 28px #3399ff",
      userSelect: "none",
      zIndex: 9300,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "0.3em",
    }} aria-label={words.join(" ")}>
      {words.map((word, i) => {
        const displayText = i === wordIndex ? word.slice(0, charCount) : i < wordIndex ? word : "";
        return (
          <motion.div key={i} initial={{ opacity: 0, scale: 0.7, rotateX: 40 }} animate={{ opacity: displayText ? 1 : 0, scale: 1, rotateX: 0 }} transition={{ type: "spring", stiffness: 260, damping: 25 }} style={{ fontSize: i === words.length - 1 ? "clamp(3rem, 12vw, 6rem)" : "clamp(1.6rem, 5vw, 2.8rem)", letterSpacing: "0.06em", filter: "drop-shadow(0 0 14px #77ccff)" }}>
            {displayText}
            <span style={{ marginLeft: 6, color: "#55bbff" }} className="cursor">|</span>
          </motion.div>
        );
      })}
    </div>
  );
};

/* ================= FineRain - Filigrane 800+ Tropfen mit Wellen & Glow ================= */
const FineRain = () => {
  const canvasRef = useRef();

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let w = (canvas.width = window.innerWidth);
    let h = (canvas.height = window.innerHeight);

    const dropsCount = 800;
    const drops = [];
    for (let i = 0; i < dropsCount; i++) {
      drops.push({
        x: Math.random() * w,
        y: Math.random() * h,
        z: Math.random(),
        length: 8 + Math.random() * 12,
        speed: 40 + Math.random() * 90,
        windPhase: Math.random() * Math.PI * 2,
        opacity: 0.08 + Math.random() * 0.18,
        baseBlue: 190 + Math.random() * 60,
      });
    }

    let animationId;

    const draw = (time = 0) => {
      ctx.clearRect(0, 0, w, h);

      const bgGrad = ctx.createLinearGradient(0, 0, 0, h);
      bgGrad.addColorStop(0, "#010519");
      bgGrad.addColorStop(1, "#04122a");
      ctx.fillStyle = bgGrad;
      ctx.fillRect(0, 0, w, h);

      drops.forEach((drop) => {
        const perspective = drop.z * 0.65 + 0.35;
        const speed = drop.speed * perspective;
        const length = drop.length * perspective;
        const windX = 20 * perspective * Math.sin(time / 1000 + drop.windPhase + drop.x / 50);

        drop.y += speed * 0.016;
        if (drop.y > h + length) drop.y = -length;

        const gradient = ctx.createLinearGradient(drop.x + windX, drop.y, drop.x + windX, drop.y + length);
        gradient.addColorStop(0, `rgba(230, 240, 255, 0)`);
        gradient.addColorStop(0.3, `rgba(200, 220, 255, ${drop.opacity * 0.35})`);
        gradient.addColorStop(0.7, `rgba(180, 205, 255, ${drop.opacity})`);
        gradient.addColorStop(1, `rgba(210, 230, 255, 0)`);

        ctx.strokeStyle = gradient;
        ctx.lineWidth = 1.3 * perspective;

        ctx.shadowColor = `rgba(100,170,255,${drop.opacity * 0.75})`;
        ctx.shadowBlur = 5 * perspective;

        ctx.beginPath();
        ctx.moveTo(drop.x + windX, drop.y);
        ctx.lineTo(drop.x + windX, drop.y + length);
        ctx.stroke();
        ctx.shadowBlur = 0;
      });

      animationId = requestAnimationFrame(draw);
    };

    draw();

    const resizeHandler = () => {
      w = canvas.width = window.innerWidth;
      h = canvas.height = window.innerHeight;
    };
    window.addEventListener("resize", resizeHandler);

    return () => {
      window.removeEventListener("resize", resizeHandler);
      cancelAnimationFrame(animationId);
    };
  }, []);

  return <canvas ref={canvasRef} style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", pointerEvents: "none", zIndex: 9000 }} />;
};

/* ================= SolarFlare - Dynamische Sonnenaura mit Mehrfach-Strahlen ================= */
const SolarFlare = () => {
  const canvasRef = useRef();
  const [pulse, setPulse] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setPulse((p) => (p + 0.02) % 3), 16);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    let w = (c.width = window.innerWidth);
    let h = (c.height = window.innerHeight);

    const draw = () => {
      ctx.clearRect(0, 0, w, h);

      const maxR = Math.min(w, h) * 0.55;
      const radius = maxR * (0.7 + 0.3 * Math.sin((pulse * Math.PI) / 1.4));

      const grad = ctx.createRadialGradient(w / 2, h / 2, radius * 0.25, w / 2, h / 2, radius);
      grad.addColorStop(0, "rgba(255,245,200,0.95)");
      grad.addColorStop(0.7, "rgba(255,230,80,0.3)");
      grad.addColorStop(1, "rgba(255,200,60,0)");

      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(w / 2, h / 2, radius, 0, 2 * Math.PI);
      ctx.fill();

      // Strahlen
      for (let i = 0; i < 24; i++) {
        const angle = (i * 15 + pulse * 20) % 360;
        const length = maxR * (0.6 + 0.15 * Math.sin(pulse * 7 + i));
        const x1 = w/2 + Math.cos(angle * Math.PI/180) * radius * 0.9;
        const y1 = h/2 + Math.sin(angle * Math.PI/180) * radius * 0.9;
        const x2 = w/2 + Math.cos(angle * Math.PI/180) * (radius + length);
        const y2 = h/2 + Math.sin(angle * Math.PI/180) * (radius + length);

        ctx.strokeStyle = `rgba(255, 230, 150, ${0.08 + 0.3 * Math.sin(i + pulse * 10)})`;
        ctx.lineWidth = 2;
        ctx.shadowColor = `rgba(255,240,180,0.6)`;
        ctx.shadowBlur = 8;

        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
        ctx.shadowBlur = 0;
      }

      requestAnimationFrame(draw);
    };
    draw();

    const onResize = () => {
      w = c.width = window.innerWidth;
      h = c.height = window.innerHeight;
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [pulse]);

  return <canvas ref={canvasRef} style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", pointerEvents: "none", zIndex: 9100 }} />;
};

/* ================= Kinesthetic „gonkle“ Schriftzug mit chromatischem Glanz ================= */
const KinestheticText = ({ onComplete }) => {
  const [letters, setLetters] = useState([]);
  const word = "gonkle";

  useEffect(() => {
    let idx = 0;
    setLetters([]);
    const interval = setInterval(() => {
      if (idx >= word.length) {
        clearInterval(interval);
        if (onComplete) onComplete();
        return;
      }
      setLetters((prev) => [...prev, word[idx]]);
      idx++;
    }, 140);
    return () => clearInterval(interval);
  }, [onComplete]);

  return (
    <div style={{
      position: "fixed", top: "25vh",
      width: "100vw", display: "flex", justifyContent: "center", gap: 22,
      fontFamily: "'Major Mono Display', monospace", fontWeight: 900,
      fontSize: "clamp(3rem, 11vw, 8rem)", color: "#ffcc88",
      textShadow: "0 0 10px #ffbb44, 0 0 30px #ffcc66, 0 0 45px #ffaa11",
      userSelect: "none", zIndex: 9400,
    }}>
      {letters.map((letter, i) => (
        <motion.span key={i}
          initial={{ opacity: 0, scale: 0.4, rotateX: 90 }}
          animate={{ opacity: 1, scale: 1, rotateX: 0 }}
          transition={{ delay: i * 0.22, type: "spring", stiffness: 650, damping: 22 }}
          style={{ display: "inline-block", filter: "drop-shadow(0 0 12px #ffbb22)" }}>
          {letter}
        </motion.span>
      ))}
    </div>
  );
};

/* ================= FlyIn mit Rotation & Scaling, stark lebendig ================= */
const directions45deg = [
  { x: -75, y: 0, rotate: -14 },
  { x: 75, y: 0, rotate: 12 },
  { x: 0, y: -75, rotate: 7 },
  { x: 0, y: 75, rotate: -9 },
  { x: -55, y: -55, rotate: -18 },
  { x: 55, y: 55, rotate: 16 },
  { x: -55, y: 55, rotate: 24 },
  { x: 55, y: -55, rotate: -22 },
];

const createRandomVariant = () => {
  const d = directions45deg[Math.floor(Math.random() * directions45deg.length)];
  return {
    hidden: { opacity: 0, x: d.x, y: d.y, scale: 0.8, rotate: d.rotate, filter: "brightness(0.65)" },
    visible: {
      opacity: 1,
      x: 0,
      y: 0,
      scale: 1,
      rotate: 0,
      filter: "brightness(1)",
      transition: { type: "spring", stiffness: 230, damping: 27, mass: 0.8 },
    },
  };
};

/* ================= Blitz mit Vibration ================= */
const SingleLightning = ({ onFinish }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const showTimer = setTimeout(() => setVisible(true), 2000);
    const hideTimer = setTimeout(() => {
      setVisible(false);
      if (onFinish) onFinish();
    }, 2400);

    if ("vibrate" in navigator) setTimeout(() => navigator.vibrate(120), 2100);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, [onFinish]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: visible ? 1 : 0 }}
      style={{
        position: "fixed", top: 0, left: 0,
        width: "100vw", height: "100vh",
        backgroundColor: "rgba(255,255,255,0.95)",
        zIndex: 9300, pointerEvents: "none", display: visible ? "flex" : "none",
      }}
    />
  );
};

/* ================= Haupt Onboarding Seite ================= */
const OnboardingPage = () => {
  const { t } = useTranslation();
  const { startSession } = useAuth();
  const [searchParams] = useSearchParams();

  const [nickname, setNickname] = useState("");
  const [selfie, setSelfie] = useState(null);
  const [selectedSport, setSelectedSport] = useState(null);
  const [currentSportTooltip, setCurrentSportTooltip] = useState(null);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showAccessKeyModal, setShowAccessKeyModal] = useState(false);

  // Abläufe: 0=Regen+Text parallel; 1=Blitz; 2=Sonne+Strahlen; 3=Formular
  const [stage, setStage] = useState(0);

  useEffect(() => {
    if (searchParams.get("key")) setShowAccessKeyModal(true);
  }, [searchParams]);

  useEffect(() => {
    let timer;
    if (stage === 0) timer = setTimeout(() => setStage(1), 4600);
    else if (stage === 1) timer = setTimeout(() => setStage(2), 700);
    else if (stage === 2) timer = setTimeout(() => setStage(3), 1100);
    return () => clearTimeout(timer);
  }, [stage]);

  const isReady = nickname.trim() && selfie && selectedSport;

  const handleSubmit = async e => {
    e.preventDefault();
    if (!isReady || loading) return;
    setError("");
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("nickname", nickname.trim());
      formData.append("selfie", selfie);
      formData.append("favorite_sport", selectedSport);
      await startSession(formData);
    } catch (err) {
      setError(err?.toString() || "Unbekannter Fehler");
      setLoading(false);
    }
  };

  const containerStyle = {
    position: "relative", height: "100vh",
    backgroundColor: "var(--color-background)",
    display: "flex", justifyContent: "center",
    alignItems: stage === 3 ? "center" : "flex-start",
    padding: "3rem 1rem", userSelect: "none",
  };

  const cardStyle = {
    maxWidth: 480, margin: "auto", padding: "3rem 2.5rem",
    borderRadius: "var(--border-radius-large)", background: "rgba(15, 21, 46, 0.85)",
    boxShadow: "0 0 60px var(--color-button-blau)", border: "2px solid var(--color-button-blau)",
    backdropFilter: "saturate(1.2)", color: "var(--color-text-weiss)", textAlign: "center",
    width: "100%", fontFamily: "var(--font-display)", boxSizing: "border-box",
  };

  const formElements = [
    { key: 'title', content: <h1 style={{ fontSize: "clamp(3rem, 8vw, 4.5rem)", marginBottom: 24, color: "var(--color-button-blau)", textShadow: "var(--text-glow-effect)", userSelect: "none", letterSpacing: "0.05em" }}>gonkle</h1> },
    { key: 'subtitle', content: <h2 style={{ fontWeight: "400", fontSize: "clamp(1.5rem, 4vw, 2rem)", marginBottom: 28, color: "var(--color-text-hellblau)" }}>{t("app_subtitle")}</h2> },
    { key: 'tagline', content: <p style={{ fontSize: "0.95rem", color: "rgba(130, 233, 255, 0.75)", marginBottom: 20, maxWidth: 350, lineHeight: 1.4 }}>Spontaneous local meetups. No registration. No spam. Just vibes.</p> },
    {
      key: 'nickname', content: <input type="text" placeholder={t("nickname_placeholder")} value={nickname} onChange={e => setNickname(e.target.value)} required autoComplete="off" spellCheck={false} style={{
        width: "100%", maxWidth: 320, padding: "18px 24px", fontSize: 22, borderRadius: "var(--border-radius-medium)",
        border: "3px solid #000", backgroundColor: "rgba(0,0,0,0.25)", color: "var(--color-text-weiss)",
        textAlign: "center", boxShadow: "0 0 18px var(--color-button-blau)", outline: "none",
      }} />
    },
    {
      key: 'selfie', content: <label style={{
        display: "block", padding: 38, backgroundColor: "rgba(0, 123, 255, 0.12)", borderRadius: "var(--border-radius-medium)",
        border: "3px dashed var(--color-button-blau)", cursor: "pointer", userSelect: "none", color: "var(--color-text-hellblau)",
        margin: "auto", fontWeight: "600", fontSize: "1.1rem", marginBottom: 20, maxWidth: 320,
      }}>{selfie ? selfie.name : t("selfie_placeholder")}
        <input type="file" accept="image/*" capture="user" onChange={e => setSelfie(e.target.files[0])} style={{ display: "none" }} required />
      </label>
    },
    {
      key: 'sportSlider', content: <div style={{ width: "100%", maxWidth: 350, marginBottom: 28 }}>
        <SportArcSlider onSelect={(val, label) => {
          setSelectedSport(val);
          setCurrentSportTooltip(label);
        }} />
        {currentSportTooltip && <div style={{
          marginTop: 8, fontSize: 14, color: "var(--color-text-hellblau)", userSelect: "none",
          textShadow: "0 0 10px var(--color-button-blau)"
        }}>{currentSportTooltip}</div>}
      </div>
    },
    { key: 'nospam', content: <p style={{ fontSize: "0.85rem", color: "rgba(255, 255, 255, 0.6)", marginBottom: 10, fontStyle: "italic", maxWidth: 320, lineHeight: 1.3 }}>We don't send you spammails, cause we don't know where to.</p> },
    { key: 'adspace', content: <p style={{ fontSize: "1.5rem", fontWeight: "700", color: "var(--color-text-hellblau)", marginBottom: 20, textShadow: "0 0 8px rgba(130, 233, 255, 0.4)", letterSpacing: "0.03em" }}>HERE COULD BE YOUR AD</p> },
    {
      key: 'submit', content: <button type="submit" disabled={!isReady || loading} style={{
        width: "100%", maxWidth: 320, padding: "22px 0", fontSize: 24, fontWeight: "bold", borderRadius: "var(--border-radius-medium)",
        background: isReady ? "linear-gradient(90deg, #00c6ff, #0066cc)" : "rgba(0,66,132,0.3)", color: "var(--color-text-weiss)",
        border: "none", cursor: isReady ? "pointer" : "not-allowed", opacity: isReady ? 1 : 0.6, boxShadow: isReady ? "0 0 30px var(--color-button-blau)" : "none",
        transition: "opacity 0.3s ease, box-shadow 0.3s ease", userSelect: "none",
      }}>{loading ? t("loading") : t("go_button")}</button>
    },
    {
      key: 'error', content: <div style={{
        color: "var(--color-danger-glow)", marginTop: 10, fontWeight: "600", fontSize: 14, minHeight: 24, userSelect: "text"
      }}>{error}</div>
    },
    {
      key: 'accessKey', content: <p role="button" tabIndex={0} onClick={() => setShowAccessKeyModal(true)} onKeyDown={e => { if (e.key === "Enter") setShowAccessKeyModal(true); }} style={{
        cursor: "pointer", marginTop: 26, fontSize: 14, color: "var(--color-button-blau)", textDecoration: "underline", userSelect: "none"
      }}>{t("got_an_access_key_link")}</p>
    }
  ];

  return (
    <div style={containerStyle}>
      {stage === 0 && <FineRain />}
      {stage === 0 && <TypewriterText words={["this", "is", "gonkle"]} />}
      {stage === 1 && <SingleLightning onFinish={() => setStage(2)} />}
      {stage === 2 && <SolarFlare />}
      {stage === 2 && <LightRays />}
      {stage === 3 && (
        <motion.form
          initial="hidden"
          animate="visible"
          variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.11, delayChildren: 0.3 } } }}
          onSubmit={handleSubmit}
          noValidate
          style={{ ...cardStyle, display: "flex", flexDirection: "column", gap: 22, alignItems: "center" }}
        >
          {formElements.map(({ key, content }) =>
            React.cloneElement(content, { key, variants: createRandomVariant(), initial: "hidden", animate: "visible" })
          )}
        </motion.form>
      )}

      <AnimatePresence>
        {showAccessKeyModal && <AccessKeyModal onClose={() => setShowAccessKeyModal(false)} />}
      </AnimatePresence>

      <motion.footer initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }} style={{
        position: "fixed", bottom: 8, width: "100%", fontSize: 12, textAlign: "center",
        color: "var(--color-text-muted)", userSelect: "none", padding: 8,
        backgroundColor: "rgba(10, 12, 25, 0.25)", backdropFilter: "blur(3px)", fontFamily: "var(--font-display)"
      }}>
        <p style={{ margin: "0 0 4px 0" }}>
          <Link to="/impressum" style={{ color: "var(--color-text-muted)", userSelect: "none" }}>Impressum</Link> |{" "}
          <Link to="/datenschutz" style={{ color: "var(--color-text-muted)", userSelect: "none" }}>Datenschutz</Link>
        </p>
        <p style={{ margin: 0, fontSize: 11, color: "rgba(255, 255, 255, 0.4)" }}>(c) nopainsoft</p>
      </motion.footer>
    </div>
  );
};

export default OnboardingPage;
